import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as db from '@/lib/data-service';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function init() {
      if (isSupabaseConfigured) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const u = { id: session.user.id, email: session.user.email || '' };
            // Clear stale local data, then sync fresh from Supabase
            db.resetDB();
            db.setSession({ user: u });
            setUser(u);
            await db.syncFromSupabase(u.id);
            const p = db.getProfile(u.id);
            const onboardedProfile = db.hasCompletedOnboarding(p) ? p : null;
            setProfile(onboardedProfile);
            if (onboardedProfile) {
              db.syncDiscoveryProfiles(u.id, onboardedProfile.seeking, onboardedProfile.city);
            }
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn('Supabase session check failed:', e);
        }
      }
      setLoading(false);
    }
    init();
  }, []);

  const sendOtp = useCallback(async (email) => {
    if (!isSupabaseConfigured) return { error: 'Supabase not configured' };
    try {
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) return { error: error.message };
      return { success: true };
    } catch (e) {
      console.error('sendOtp failed:', e?.message || e);
      return { error: e.message || 'Failed to send OTP. Please try again.' };
    }
  }, []);

  const verifyOtp = useCallback(async (email, code) => {
    if (code.length !== 6) return { error: 'Enter a 6-digit code' };
    if (!isSupabaseConfigured) return { error: 'Supabase not configured' };
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'email',
      });
      if (error) return { error: error.message };
      const sessionUser = data?.session?.user || data?.user;
      if (sessionUser) {
        const u = { id: sessionUser.id, email };
        // Clear all stale local data before syncing
        db.resetDB();
        db.setSession({ user: u });
        await db.syncFromSupabase(u.id);
        const p = db.getProfile(u.id);
        if (p && p.admin_blocked === true) {
          await supabase.auth.signOut();
          return { error: 'Your account has been suspended. Contact hello@jivapp.in for support.' };
        }
        setUser(u);
        const onboardedProfile = db.hasCompletedOnboarding(p) ? p : null;
        setProfile(onboardedProfile);
        if (onboardedProfile) db.syncDiscoveryProfiles(u.id, onboardedProfile.seeking, onboardedProfile.city);
        return { success: true, isNew: !onboardedProfile };
      }
      return { error: 'Verification failed. Please try again.' };
    } catch (e) {
      console.error('verifyOtp failed:', e?.message || e);
      return { error: e.message || 'Verification failed. Please try again.' };
    }
  }, []);

  const logout = useCallback(() => {
    db.clearSession();
    setUser(null);
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(() => {
    if (user) {
      const p = db.getProfile(user.id);
      setProfile(db.hasCompletedOnboarding(p) ? p : null);
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, profile, loading, sendOtp, verifyOtp, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
